import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  trackBeginCheckout,
  trackTrialMembershipOfferClicked,
  trackTrialMembershipOfferDismissed,
  trackTrialMembershipOfferShown,
  trackTrialMembershipPopupClick,
  trackTrialMembershipRulesOpened,
} from "@/lib/dataLayer";
import { getStoredGclid } from "@/lib/attribution";
import { getReferralId } from "@/lib/referral";
import { savePendingCheckout } from "@/lib/pendingCheckout";
import { trpc } from "@/lib/trpc";
import { Info, Loader2 } from "lucide-react";
import { toast } from "sonner";

// The two PROACTIVE trial popups (duration-choice in Director chat and
// post-render after watching the finished video) are paused while we decide
// between the $9.90 trial and a new-user discount on the normal plans
// (2026-07-10). Re-enable with VITE_TRIAL_MEMBERSHIP_POPUPS=true. The
// top-up-claim gate (free users can't buy credit packs) and the
// resume-checkout banner (user already started a trial checkout) still use
// this modal regardless of the flag.
export const TRIAL_MEMBERSHIP_POPUPS_ENABLED =
  import.meta.env.VITE_TRIAL_MEMBERSHIP_POPUPS === "true";

type TrialMembershipDismissReason =
  | "dialog_close"
  | "close_button"
  | "quick_short";

type TrialMembershipModalVariant =
  | "duration_choice"
  | "post_render_claim"
  | "topup_claim"
  | "resume_claim";

type TrialMembershipModalProps = {
  open: boolean;
  onClose: (reason?: TrialMembershipDismissReason) => void;
  onViewPlans?: () => void;
  trigger?: string;
  variant?: TrialMembershipModalVariant;
};

export default function TrialMembershipModal({
  open,
  onClose,
  onViewPlans,
  trigger = "trial_video_completed",
  variant: variantProp,
}: TrialMembershipModalProps) {
  const [showRules, setShowRules] = useState(false);
  const trackedOpenRef = useRef(false);
  const variant =
    variantProp ??
    (trigger === "trial_video_completed"
      ? "post_render_claim"
      : "duration_choice");
  const isPostRenderClaim = variant === "post_render_claim";
  const isTopupClaim = variant === "topup_claim";
  const isResumeClaim = variant === "resume_claim";
  // Claim-style popups (post-render, top-up, resume) drop the
  // duration-choice buttons and use a plain "Claim" CTA.
  const isClaimStyle = isPostRenderClaim || isTopupClaim || isResumeClaim;
  const analyticsContext = {
    source: isPostRenderClaim
      ? "trial_membership_post_render_popup"
      : isTopupClaim
        ? "trial_membership_topup_popup"
        : isResumeClaim
          ? "trial_membership_resume_banner"
          : "trial_membership_duration_popup",
    popup_variant: variant,
    popup_stage: isPostRenderClaim
      ? "post_render"
      : isTopupClaim
        ? "topup"
        : isResumeClaim
          ? "resume"
          : "duration_choice",
    popup_placement: isPostRenderClaim
      ? "after_video_render"
      : isTopupClaim
        ? "upgrade_modal_topup"
        : isResumeClaim
          ? "resume_checkout_banner"
          : "chat_duration",
  };
  const primaryActionLabel = isClaimStyle ? "Claim" : "Make longer video";
  const primaryClickTarget = isPostRenderClaim
    ? "claim_post_render_trial"
    : isTopupClaim
      ? "claim_topup_trial"
      : isResumeClaim
        ? "claim_resume_trial"
        : "claim_generate_longer_video";
  const createCheckout = trpc.payment.createTrialMembershipCheckout.useMutation(
    {
      onSuccess: (data) => {
        if (data.url) {
          window.location.href = data.url;
        }
      },
      onError: (error) => {
        toast.error(error.message || "Could not start checkout");
      },
    },
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("trial-membership-plus-jakarta")) return;
    const link = document.createElement("link");
    link.id = "trial-membership-plus-jakarta";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (open && !trackedOpenRef.current) {
      trackedOpenRef.current = true;
      trackTrialMembershipOfferShown({
        ...analyticsContext,
        trigger,
        value: 9.9,
      });
      return;
    }

    if (!open) {
      trackedOpenRef.current = false;
      setShowRules(false);
    }
  }, [open, trigger, variant]);

  const trackPopupClick = (clickTarget: string, actionLabel: string) => {
    trackTrialMembershipPopupClick({
      ...analyticsContext,
      trigger,
      click_target: clickTarget,
      action_label: actionLabel,
    });
  };

  const handleDismiss = (
    dismissReason: TrialMembershipDismissReason,
    clickTarget?: string,
    actionLabel?: string,
  ) => {
    if (clickTarget && actionLabel) {
      trackPopupClick(clickTarget, actionLabel);
    }
    trackTrialMembershipOfferDismissed({
      ...analyticsContext,
      trigger,
      dismiss_reason: dismissReason,
    });
    onClose(dismissReason);
  };

  const handleClose = () => {
    handleDismiss("dialog_close");
  };

  const handleCloseButtonClick = () => {
    handleDismiss("close_button", "close_button", "Close offer");
  };

  const handleQuickShortClick = () => {
    handleDismiss("quick_short", "quick_short", "Continue as quick short");
  };

  const handleClaim = () => {
    trackPopupClick(primaryClickTarget, primaryActionLabel);
    trackTrialMembershipOfferClicked({
      ...analyticsContext,
      trigger,
      click_target: primaryClickTarget,
      action_label: primaryActionLabel,
    });
    trackBeginCheckout(
      [
        {
          item_id: "plus_trial",
          item_name: "New Member Plus Trial",
          price: 9.9,
          quantity: 1,
          item_category: "subscription",
          item_variant: "7_day_intro",
        },
      ],
      9.9,
      "USD",
      { trigger, source: analyticsContext.source },
    );
    savePendingCheckout({
      kind: "trial",
      planId: "plus_trial",
      planName: "Plus 7-Day Trial",
      price: 9.9,
    });
    createCheckout.mutate({
      gclid: getStoredGclid() ?? undefined,
      clientReferenceId: getReferralId(),
    });
  };

  const handleViewPlans = () => {
    trackPopupClick("view_all_plans", "View all plans");
    if (onViewPlans) {
      onViewPlans();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="max-h-[92svh] w-[min(460px,calc(100vw-1.25rem))] overflow-y-auto border-0 bg-transparent p-0 text-white shadow-none sm:max-w-none"
        style={{
          fontFamily:
            "'Plus Jakarta Sans', Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <DialogTitle className="sr-only">
          {showRules
            ? "7-Day New Member Plus Trial rules"
            : "Trial membership offer"}
        </DialogTitle>

        <div className="relative rounded-[28px] bg-[linear-gradient(160deg,rgba(150,140,255,0.4)_0%,rgba(90,70,180,0.08)_50%,rgba(120,60,110,0.18)_100%)] p-px shadow-[0_38px_90px_-24px_rgba(0,0,0,0.82)]">
          <div className="relative overflow-hidden rounded-[27px] bg-[linear-gradient(170deg,#241b4e_0%,#211842_45%,#281836_100%)]">
            <button
              type="button"
              aria-label="Close offer"
              onClick={handleCloseButtonClick}
              className="absolute right-4 top-4 z-20 grid h-9 w-9 place-items-center rounded-full border-0 bg-white/[0.12] text-base leading-none text-white/75 transition-colors hover:bg-white/[0.22] hover:text-white"
            >
              ✕
            </button>

            {showRules ? (
              <div className="px-7 py-8 sm:px-8 sm:py-9">
                <div className="mb-6 flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-violet-500/18 text-violet-200">
                    <Info className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h2 className="pr-10 text-2xl font-bold leading-tight">
                    7-Day New Member Plus Trial
                  </h2>
                </div>

                <div className="space-y-5 text-[15px] leading-7 text-white/74">
                  <section>
                    <h3 className="mb-1 font-semibold text-white">
                      Perk Contents
                    </h3>
                    <p>
                      After purchasing the 7-day trial, your account receives
                      Plus access and a one-time grant of{" "}
                      <strong className="text-white">500 credits</strong>.
                    </p>
                  </section>

                  <section>
                    <h3 className="mb-1 font-semibold text-white">
                      Renewal Mechanism
                    </h3>
                    <p>
                      The first 7 days are billed at{" "}
                      <strong className="text-white">$9.90</strong>. After that,
                      the subscription automatically continues on the{" "}
                      <strong className="text-white">Plus Monthly Plan</strong>{" "}
                      at <strong className="text-white">$35/month</strong>.
                    </p>
                  </section>

                  <section>
                    <h3 className="mb-1 font-semibold text-white">
                      Cancellation
                    </h3>
                    <p>
                      You can cancel auto-renewal from billing before the 7-day
                      period ends. Your Plus access remains active until the end
                      of that paid trial period.
                    </p>
                  </section>

                  <section>
                    <h3 className="mb-1 font-semibold uppercase text-white">
                      Important Note
                    </h3>
                    <p>
                      This is a new-member offer for free users who have
                      completed the one-time trial music video. Existing paid
                      subscribers are not eligible.
                    </p>
                  </section>
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    trackPopupClick("back_to_offer", "Back to offer");
                    setShowRules(false);
                  }}
                  className="mt-7 h-12 w-full rounded-xl bg-white text-[#15131f] font-semibold hover:bg-white/90"
                >
                  Back to offer
                </Button>
              </div>
            ) : (
              <>
                <div className="pointer-events-none absolute left-1/2 top-[-86px] h-[340px] w-[340px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(111,99,240,0.28)_0%,rgba(111,99,240,0)_70%)]" />

                <div className="relative px-6 pb-5 pt-7 sm:px-8 sm:pb-8 sm:pt-8">
                  <div className="mb-4 flex flex-wrap items-center justify-center gap-2.5 pr-8 sm:mb-6">
                    <span className="text-base" aria-hidden="true">
                      💎
                    </span>
                    <span className="text-[14px] font-bold uppercase tracking-[1.4px] text-white/[0.92]">
                      Trial Membership
                    </span>
                    <span className="whitespace-nowrap rounded-md bg-[linear-gradient(135deg,#ff7a59,#ff5b7f)] px-2.5 py-1 text-[11px] font-bold text-white">
                      New User Only
                    </span>
                  </div>

                  <div className="mb-4 flex items-center justify-center gap-3.5 sm:mb-6">
                    <div className="flex items-start text-white [line-height:0.9]">
                      <span className="mt-1 text-[26px] font-bold sm:mt-2 sm:text-[34px]">
                        $
                      </span>
                      <span className="text-[52px] font-extrabold tracking-[-1px] sm:text-[76px]">
                        9.9
                      </span>
                    </div>
                    <span className="whitespace-nowrap rounded-xl border border-white/10 bg-white/10 px-3.5 py-1.5 text-[15px] font-bold text-white/90 sm:px-4 sm:py-2 sm:text-base">
                      7 Days
                    </span>
                  </div>

                  <div className="mb-2.5 flex items-center gap-3 rounded-[14px] border-[1.5px] border-[rgba(150,140,255,0.55)] bg-[rgba(111,99,240,0.12)] px-5 py-3 sm:mb-3 sm:py-4">
                    <span
                      className="h-5 w-5 flex-shrink-0 rounded-full border-[5px] border-white bg-[#6f63f0]"
                      aria-hidden="true"
                    />
                    <span className="text-xl font-extrabold text-white sm:text-[26px]">
                      500 Credits
                    </span>
                  </div>

                  <p className="mb-4 mt-0 text-center text-[13px] font-medium text-white/50 sm:mb-5">
                    Renews as Plus Monthly. Cancel anytime.
                  </p>

                  <div className="mb-4 grid grid-cols-3 items-stretch justify-center gap-2 sm:mb-6">
                    {[
                      ["🎬", "Longer"],
                      ["🖥️", "1080P"],
                      ["✨", "No Watermark"],
                    ].map(([icon, label]) => (
                      <div
                        key={label}
                        className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-xl border border-white/[0.07] bg-white/[0.06] px-1.5 py-2.5 sm:py-3"
                      >
                        <span className="text-[17px]" aria-hidden="true">
                          {icon}
                        </span>
                        <span className="text-center text-xs font-semibold text-white/[0.85]">
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleClaim}
                    disabled={createCheckout.isPending}
                    className="mb-2.5 flex w-full items-center justify-center gap-2 rounded-[13px] border-0 bg-[linear-gradient(135deg,#6f63f0,#5a4ee0)] px-4 py-3.5 text-base font-bold text-white shadow-[0_12px_28px_-8px_rgba(111,99,240,0.7)] transition-colors hover:bg-[linear-gradient(135deg,#7d72ff,#6358ec)] disabled:cursor-not-allowed disabled:opacity-70 sm:mb-3 sm:py-4"
                  >
                    {createCheckout.isPending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Redirecting
                      </>
                    ) : (
                      <>
                        {!isClaimStyle && (
                          <span aria-hidden="true">✨</span>
                        )}
                        {primaryActionLabel}
                      </>
                    )}
                  </button>

                  {!isClaimStyle && (
                    <button
                      type="button"
                      onClick={handleQuickShortClick}
                      disabled={createCheckout.isPending}
                      className="mb-4 w-full rounded-[13px] border border-white/[0.12] bg-white/[0.05] px-4 py-3 text-[15px] font-semibold text-white/[0.85] transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50 sm:mb-5 sm:py-3.5"
                    >
                      Continue as quick short
                    </button>
                  )}

                  <div
                    className={`flex items-center justify-center gap-4 ${isClaimStyle ? "mt-2" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={handleViewPlans}
                      className="border-0 bg-transparent p-0 text-[13px] font-semibold text-white/60 transition-colors hover:text-white/90"
                    >
                      View all plans
                    </button>
                    <span className="h-3.5 w-px bg-white/[0.18]" />
                    <button
                      type="button"
                      onClick={() => {
                        trackPopupClick("activity_rules", "Activity Rules");
                        trackTrialMembershipRulesOpened({
                          ...analyticsContext,
                          trigger,
                          click_target: "activity_rules",
                          action_label: "Activity Rules",
                        });
                        setShowRules(true);
                      }}
                      className="border-0 bg-transparent p-0 text-[13px] font-semibold text-white/60 transition-colors hover:text-white/90"
                    >
                      Activity Rules
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
